package info.rezident.app.ui.components.widgets

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.functions.functions
import info.rezident.app.core.AppTracking
import info.rezident.app.core.Logger
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.payments.StripeCatalogRepository
import info.rezident.app.data.user.UserRepository
import info.rezident.app.data.user.VariantsRepository
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.serialization.Serializable

/**
 * Request model for createCheckoutSession Cloud Function
 */
@Serializable
data class CreateCheckoutSessionRequest(
    val priceId: String,
    val quantity: Int,
    val successUrl: String,
    val cancelUrl: String,
    val quizId: String?
)

/**
 * Response model for createCheckoutSession Cloud Function
 */
@Serializable
data class CreateCheckoutSessionResponse(
    val url: String,
    val id: String
)

/**
 * Helper object for handling Stripe checkout flow
 */
object CheckoutHelper {

    /**
     * Start checkout process for recommendations unlock
     *
     * @param quizId The quiz ID to associate with the checkout
     * @param selectedPriceId Optional specific price ID to use
     * @param selectedPriceText Optional price text for analytics
     * @param onCheckoutStateChange Callback to update UI state (isCreating: Boolean)
     * @param onNavigate Callback to navigate to URL (either auth page or Stripe checkout)
     * @param onError Callback to handle error messages
     */
    suspend fun startCheckout(
        quizId: String?,
        selectedPriceId: String? = null,
        selectedPriceText: String? = null,
        onCheckoutStateChange: (Boolean) -> Unit,
        onNavigate: (String) -> Unit,
        onError: ((String) -> Unit)? = null
    ) {
        Logger.log("[CheckoutHelper] startCheckout called with quizId=$quizId, selectedPriceId=$selectedPriceId")
        onCheckoutStateChange(true)

        try {
            val currentUser = AuthRepository.getCurrentUser()
            Logger.log("[CheckoutHelper] Current user: ${currentUser?.uid}")
            if (currentUser == null) {
                Logger.log("[CheckoutHelper] No user, navigating to /conectare")
                onNavigate("/conectare")
                return
            }

            val origin = window.location.origin
            val successUrl = buildString {
                append("$origin/recomandari")
                quizId?.let { append("?quizId=$it") }
            }
            val cancelUrl = buildString {
                append("$origin/recomandari")
                quizId?.let { append("?quizId=$it") }
            }

            // Use provided price if available, otherwise fallback to first active price
            Logger.log("[CheckoutHelper] selectedPriceId: $selectedPriceId")
            val priceId = if (!selectedPriceId.isNullOrBlank()) {
                Logger.log("[CheckoutHelper] Using selectedPriceId: $selectedPriceId")
                selectedPriceId
            } else {
                Logger.log("[CheckoutHelper] selectedPriceId is null/blank, fetching fallback price...")
                val fallbackPrice = StripeCatalogRepository.getFirstActiveOneTimePriceId()
                Logger.log("[CheckoutHelper] Fallback price from catalog: $fallbackPrice")
                fallbackPrice
            }
            Logger.log("[CheckoutHelper] Final priceId: $priceId")
            if (priceId.isNullOrBlank()) {
                val errorMsg = "Nu am găsit un preț activ. Te rugăm să încerci din nou."
                Logger.error("[CheckoutHelper] No active price found in catalog. Cannot proceed with checkout.")
                onCheckoutStateChange(false)
                onError?.invoke(errorMsg)
                AppTracking.logEvent(
                    AppTracking.EVENT_ERROR_DISPLAYED,
                    mapOf(
                        "error_type" to "payment_no_price",
                        "location" to "recommendations_checkout"
                    )
                )
                return
            }

            // Get user context for analytics
            val userProfile = UserRepository.user.value
            val academicYear = userProfile?.academicYear ?: "unknown"
            val priceVariant = userProfile?.priceVariant?.name ?: "unknown"
            val recommendationsVariant = VariantsRepository.recommendationsVariantLabelOrNull() ?: "unknown"

            // Emit begin_checkout with context
            AppTracking.logEvent(
                AppTracking.EVENT_BEGIN_CHECKOUT,
                mapOf(
                    AppTracking.PARAM_TEMPLATE_ID to 1,
                    "price_id" to priceId,
                    "currency" to "RON",
                    "value" to (selectedPriceText?.split(" ")?.firstOrNull() ?: ""),
                    AppTracking.PARAM_ACADEMIC_YEAR to academicYear,
                    AppTracking.PARAM_PRICE_VARIANT to priceVariant,
                    AppTracking.PARAM_RECOMMENDATIONS_VARIANT to recommendationsVariant,
                    "items" to mapOf(
                        "item_id" to "1",
                        "item_name" to "Recomandări Complete"
                    )
                )
            )

            // Call Cloud Function to create checkout session using Firebase Functions SDK
            val functions = Firebase.functions("europe-west4")

            val requestData = CreateCheckoutSessionRequest(
                priceId = priceId,
                quantity = 1,
                successUrl = successUrl,
                cancelUrl = cancelUrl,
                quizId = quizId
            )

            Logger.log("[CheckoutHelper] Calling createCheckoutSession Cloud Function with priceId: $priceId")

            // Call the Cloud Function
            val callable = functions.httpsCallable("createCheckoutSession")
            val result = callable.invoke(requestData)

            // Parse the response - the function returns { url: string, id: string }
            val responseData = result.data<CreateCheckoutSessionResponse>()
            val url = responseData.url
            if (url.isNotBlank()) {
                Logger.log("Stripe checkout session created successfully. URL: $url")
                onNavigate(url)

                // Delayed enabling the button until user is back to recs page
                delay(500)

                // Reset checkout state after successful session creation
                onCheckoutStateChange(false)
            } else {
                val errorMsg = "Sesiunea de plată nu a putut fi creată. Te rugăm să încerci din nou."
                Logger.error("Stripe session has no URL.")
                onCheckoutStateChange(false)
                onError?.invoke(errorMsg)
                AppTracking.logEvent(
                    AppTracking.EVENT_ERROR_DISPLAYED,
                    mapOf(
                        "error_type" to "payment_no_session_url",
                        "location" to "recommendations_checkout"
                    )
                )
            }
        } catch (t: Throwable) {
            val errorMsg = "A apărut o eroare la procesarea plății. Te rugăm să încerci din nou."
            Logger.error("Failed to start checkout: $t")
            onCheckoutStateChange(false)
            onError?.invoke(errorMsg)
            AppTracking.logEvent(
                AppTracking.EVENT_ERROR_DISPLAYED,
                mapOf(
                    "error_type" to "payment_checkout_exception",
                    "location" to "recommendations_checkout",
                    "message" to (t.message ?: "unknown")
                )
            )
        }
    }
}
